import {
  anciennete,
  caMensuel,
  CLASSES_SYSCOHADA,
  classeCompte,
  creances,
  equilibre,
  journalParPiece,
  LIBELLE_JOURNAL,
  libelleCompte,
  LIBELLE_REPARTITION,
  planComplet,
  repartitionCA,
  resultatParFlux,
  type Ecriture,
} from '../../api/compta'
import { dateCourte, fcfa } from '../Format'
import { Icone } from '../Icone'
import { Anneau, Barres, CourbeCA, Jauge } from './Graphiques'
import { Carte, CodeCompte, exporterCSV, Ligne, Table, type PropsCompta } from './briques'

const DR_CR = (s: number) => `${fcfa(Math.abs(s))} ${s >= 0 ? 'DR' : 'CR'}`

/* ---------------------------------------------------------------- tableau */

export function Tableau({ donnees, ecritures, compte }: PropsCompta) {
  const flux = resultatParFlux(ecritures)
  const age = anciennete(donnees)
  const plan = donnees.planComptable
  const repartition = repartitionCA(donnees)
  const couleursCharges = ['--err', '--warn', '--info', '--gold-dark', '--muted']
  return (
    <>
      <div className="chart-grid">
        <div className="card chart-card">
          <div className="chart-h">
            <span className="h-ic">
              <Icone nom="chart" />
            </span>{' '}
            CA reconnu par mois
          </div>
          <CourbeCA lignes={caMensuel(ecritures)} />
        </div>
        <div className="card chart-card">
          <div className="chart-h">
            <span className="h-ic">
              <Icone nom="scale" />
            </span>{' '}
            CA par flux
          </div>
          <Anneau
            parts={[
              { libelle: 'Outsourcing', valeur: Math.max(0, flux.out.produits), couleur: '--gold-dark' },
              { libelle: 'SaaS', valeur: Math.max(0, flux.saas.produits), couleur: '--gold' },
              { libelle: 'Commun', valeur: Math.max(0, flux.com.produits), couleur: '--gold-light' },
            ]}
          />
        </div>
        <div className="card chart-card">
          <div className="chart-h">
            <span className="h-ic">
              <Icone nom="book" />
            </span>{' '}
            Charges par compte (classe 6)
          </div>
          <Barres
            vide="Aucune charge enregistrée"
            items={Object.entries(compte.charges)
              .sort(([, a], [, b]) => b - a)
              .map(([c, v], i) => ({ libelle: libelleCompte(c, plan), valeur: v, couleur: couleursCharges[i % couleursCharges.length] ?? '--muted' }))}
          />
        </div>
        <div className="card chart-card">
          <div className="chart-h">
            <span className="h-ic">
              <Icone nom="scale" />
            </span>{' '}
            Produits et charges
          </div>
          <Jauge produits={compte.totalProduits} charges={compte.totalCharges} resultat={compte.resultat} />
        </div>
        <div className="card chart-card">
          <div className="chart-h">
            <span className="h-ic">
              <Icone nom="receipt" />
            </span>{' '}
            CA 7061 par nature (HT)
          </div>
          <Barres
            vide="Aucun chiffre d’affaires"
            items={LIBELLE_REPARTITION.map((l, i) => ({
              libelle: l.libelle,
              valeur: Math.max(0, repartition[l.cle]),
              couleur: ['--gold-dark', '--gold', '--info', '--warn', '--muted', '--gold-light', '--err'][i % 7] ?? '--muted',
            })).filter((x) => x.valeur > 0)}
          />
        </div>
        {age.total > 0 && (
          <div className="card chart-card">
            <div className="chart-h">
              <span className="h-ic">
                <Icone nom="wallet" />
              </span>{' '}
              Balance âgée des créances (411)
            </div>
            <Barres
              vide=""
              items={[
                { libelle: '0–30 j', valeur: age.j0_30, couleur: '--gold-dark' },
                { libelle: '30–60 j', valeur: age.j30_60, couleur: '--gold' },
                { libelle: '60–90 j', valeur: age.j60_90, couleur: '--warn' },
                { libelle: '+ 90 j', valeur: age.plus90, couleur: '--err' },
              ]}
            />
          </div>
        )}
      </div>
      <div className="dash-note">
        Modèle PRINCIPAL SYSCOHADA : chiffre d’affaires = tout ce qui est facturé ou livré (7061 Outsourcing ; 7063 et 7068
        par écriture manuelle). Un particulier est constaté à la livraison en CRÉANCE (411) ; la trésorerie n’apparaît qu’à
        l’encaissement ENREGISTRÉ. Trésorerie, CA, produits d’avance (487) et créances (411) restent distincts. Montants en FCFA.
      </div>
    </>
  )
}

/* ---------------------------------------------------------------- journal */

export function Journal({ ecritures, donnees, surNotifier }: PropsCompta) {
  const groupes = journalParPiece(ecritures)
  const ctl = equilibre(ecritures)
  const plan = donnees.planComptable
  return (
    <Carte
      titre="Journal général"
      icone="book"
      sous={`${groupes.length} écriture${groupes.length > 1 ? 's' : ''} · une ligne par mouvement, débit puis crédit`}
      surExport={() =>
        exporterCSV(
          'journal-syscohada.csv',
          [['Date', 'Pièce', 'Journal', 'Compte', 'Libellé', 'Débit', 'Crédit'], ...ecritures.map((e) => [dateCourte(e.date), e.piece, e.journal, e.compte, e.libelle, e.debit || '', e.credit || ''])],
          surNotifier,
        )
      }
    >
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Pièce</th>
              <th>Jnl</th>
              <th>Compte débité</th>
              <th>Compte crédité</th>
              <th style={{ textAlign: 'right' }}>Montant</th>
              <th>Libellé</th>
            </tr>
          </thead>
          <tbody>
            {groupes.length === 0 && (
              <tr>
                <td colSpan={7} className="t-muted">
                  Aucune écriture — ni course livrée, ni facture, ni dépense, ni écriture manuelle.
                </td>
              </tr>
            )}
            {groupes.flatMap((g) =>
              g.lignes.map((e, i) => (
                <tr key={`${g.piece}-${g.date}-${i}`} className={i === 0 ? 'jrn-entry' : undefined}>
                  <td className="t-muted">{i === 0 ? dateCourte(g.date) : ''}</td>
                  <td className="mono">{i === 0 ? g.piece : ''}</td>
                  <td>{i === 0 && <span className="jrn-pill" title={LIBELLE_JOURNAL[g.journal]}>{g.journal}</span>}</td>
                  <td>
                    {e.debit > 0 && (
                      <>
                        <CodeCompte code={e.compte} /> <span className="t-muted">{libelleCompte(e.compte, plan)}</span>
                      </>
                    )}
                  </td>
                  <td>
                    {e.credit > 0 && (
                      <>
                        <span style={{ display: 'inline-block', width: 16 }} />
                        <CodeCompte code={e.compte} /> <span className="t-muted">{libelleCompte(e.compte, plan)}</span>
                      </>
                    )}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {fcfa(e.debit || e.credit)}
                  </td>
                  <td className="t-muted">{e.libelle}</td>
                </tr>
              )),
            )}
            <tr style={{ background: 'var(--card)' }}>
              <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>
                Total mouvementé (débit = crédit)
              </td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                {fcfa(ctl.debit)}
              </td>
              <td>{ctl.ecart === 0 ? 'équilibré' : <b style={{ color: 'var(--err)' }}>déséquilibre de {fcfa(ctl.ecart)}</b>}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Carte>
  )
}

/* ------------------------------------------------------------ grand-livre */

export function GrandLivre({ ecritures, soldes, surNotifier }: PropsCompta) {
  const parCompte = new Map<string, Ecriture[]>()
  for (const e of ecritures) parCompte.set(e.compte, [...(parCompte.get(e.compte) ?? []), e])
  return (
    <Carte
      titre="Grand-livre"
      icone="book"
      sous="Mouvements par compte, solde progressif"
      surExport={() =>
        exporterCSV(
          'grand-livre-syscohada.csv',
          [
            ['Compte', 'Date', 'Pièce', 'Libellé', 'Débit', 'Crédit'],
            ...soldes.flatMap((s) => (parCompte.get(s.code) ?? []).map((e) => [`${s.code} ${s.libelle}`, dateCourte(e.date), e.piece, e.libelle, e.debit || '', e.credit || ''])),
          ],
          surNotifier,
        )
      }
    >
      {soldes.length === 0 && <div className="ch-empty">Aucun mouvement</div>}
      {soldes.map((s) => {
        let cumul = 0
        return (
          <div key={s.code} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <CodeCompte code={s.code} /> {s.libelle}
              <span className="t-muted" style={{ marginLeft: 'auto' }}>
                solde {DR_CR(s.solde)}
              </span>
            </div>
            <Table
              entetes={['Date', 'Pièce', 'Libellé', 'Débit', 'Crédit', 'Solde']}
              droite={[3, 4, 5]}
              vide=""
              lignes={(parCompte.get(s.code) ?? []).map((e) => {
                cumul += e.debit - e.credit
                return [
                  dateCourte(e.date),
                  <span className="mono">{e.piece}</span>,
                  <span className="t-muted">{e.libelle}</span>,
                  <span className="num-d">{e.debit ? fcfa(e.debit) : ''}</span>,
                  <span className="num-c">{e.credit ? fcfa(e.credit) : ''}</span>,
                  <b>{DR_CR(cumul)}</b>,
                ]
              })}
            />
          </div>
        )
      })}
    </Carte>
  )
}

/* ---------------------------------------------------------------- balance */

export function Balance({ soldes, surNotifier }: PropsCompta) {
  const td = soldes.reduce((s, r) => s + r.debit, 0)
  const tc = soldes.reduce((s, r) => s + r.credit, 0)
  return (
    <Carte
      titre="Balance générale"
      icone="scale"
      sous={`${soldes.length} compte${soldes.length > 1 ? 's' : ''} mouvementé${soldes.length > 1 ? 's' : ''}`}
      surExport={() =>
        exporterCSV('balance-syscohada.csv', [['Compte', 'Libellé', 'Débit', 'Crédit', 'Solde'], ...soldes.map((s) => [s.code, s.libelle, s.debit, s.credit, s.solde])], surNotifier)
      }
    >
      <Table
        entetes={['Compte', 'Libellé', 'Débit', 'Crédit', 'Solde']}
        droite={[2, 3, 4]}
        vide="Aucun compte mouvementé"
        lignes={soldes.map((s) => [
          <CodeCompte code={s.code} />,
          s.libelle,
          <span className="num-d">{fcfa(s.debit)}</span>,
          <span className="num-c">{fcfa(s.credit)}</span>,
          <b>{DR_CR(s.solde)}</b>,
        ])}
        pied={['Totaux', '', fcfa(td), fcfa(tc), td === tc ? 'équilibrée' : 'déséquilibre']}
      />
    </Carte>
  )
}

/* --------------------------------------------------------------- résultat */

export function ResultatVue({ ecritures, compte, donnees }: PropsCompta) {
  const f = resultatParFlux(ecritures)
  const plan = donnees.planComptable
  const pnl = (titre: string, sous: string, d: { produits: number; charges: number }) => {
    const res = d.produits - d.charges
    const marge = d.produits ? Math.round((res / d.produits) * 100) : 0
    return (
      <Carte titre={titre} sous={sous} flush={false}>
        <Ligne libelle="Produits" valeur={<span className="num-c">{fcfa(d.produits)}</span>} />
        <Ligne libelle="Charges" valeur={<span className="num-d">{fcfa(d.charges)}</span>} />
        <Ligne libelle={`Résultat · marge ${marge} %`} valeur={fcfa(res)} fort couleur={res >= 0 ? 'var(--green)' : 'var(--err)'} />
      </Carte>
    )
  }
  return (
    <>
      <div className="result-card">
        <div className="result-grid">
          <div>
            <div className="rg-l">Produits (classe 7)</div>
            <div className="rg-v pos">{fcfa(compte.totalProduits)}</div>
          </div>
          <div>
            <div className="rg-l">Charges (classe 6)</div>
            <div className="rg-v">{fcfa(compte.totalCharges)}</div>
          </div>
          <div>
            <div className="rg-l">Résultat net</div>
            <div className={`rg-v ${compte.resultat >= 0 ? 'pos' : 'neg'}`}>{fcfa(compte.resultat)}</div>
          </div>
        </div>
      </div>
      <div className="grid-2" style={{ marginTop: 16 }}>
        {pnl('P&L Outsourcing', 'Courses et factures · charges livreurs et flotte', f.out)}
        {pnl('P&L SaaS', 'Abonnements et mises en service (écritures manuelles)', f.saas)}
      </div>
      {(f.com.produits !== 0 || f.com.charges !== 0) && (
        <Carte titre="Structure (commun)" flush={false}>
          <Ligne libelle="Charges de structure nettes" valeur={<span className="num-d">{fcfa(f.com.charges - f.com.produits)}</span>} />
        </Carte>
      )}
      <RepartitionVue donnees={donnees} />
      <div className="grid-2">
        <Carte titre="Produits" sous={fcfa(compte.totalProduits)}>
          <Table
            entetes={['Compte', 'Libellé', 'Montant']}
            droite={[2]}
            vide="Aucun produit"
            lignes={Object.entries(compte.produits)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([c, v]) => [<CodeCompte code={c} />, libelleCompte(c, plan), <span className="num-c">{fcfa(v)}</span>])}
          />
        </Carte>
        <Carte titre="Charges" sous={fcfa(compte.totalCharges)}>
          <Table
            entetes={['Compte', 'Libellé', 'Montant']}
            droite={[2]}
            vide="Aucune charge"
            lignes={Object.entries(compte.charges)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([c, v]) => [<CodeCompte code={c} />, libelleCompte(c, plan), <span className="num-d">{fcfa(v)}</span>])}
          />
        </Carte>
      </div>
      <div className="dash-note" style={{ textAlign: 'center' }}>
        Compte de résultat simplifié, en soldes nets. Le bilan, le tableau des flux et les notes annexes sont établis par
        l’expert-comptable à partir des exports Journal, Grand-livre et Balance.
      </div>
    </>
  )
}

/* -------------------------------------------------------- créances & PCA */

export function Creances({ donnees, soldes }: PropsCompta) {
  const age = anciennete(donnees)
  const ouvertes = creances(donnees).sort((a, b) => b.age - a.age)
  const pca = -(soldes.find((s) => s.code === '487')?.solde ?? 0)
  return (
    <>
      <Carte titre="Créances clients B2B (411)" icone="receipt" sous="Factures émises non réglées + courses livrées de particuliers non encaissées (reste dû). Provision 491 au-delà de 90 jours.">
        <Table
          entetes={['Client', 'Pièce', 'Date', 'Montant', 'Âge', 'Statut']}
          droite={[3]}
          vide="Aucune créance ouverte."
          lignes={ouvertes.map((x) => [
            <span className="t-strong">{x.client}</span>,
            <span className="mono">{x.piece}</span>,
            dateCourte(x.date),
            <span className="mono">{fcfa(x.montant)}</span>,
            `${x.age} j`,
            x.age > 90 ? (
              <span className="badge b-red plain">Douteuse</span>
            ) : x.age > 30 ? (
              <span className="badge b-warn plain">En retard</span>
            ) : (
              <span className="badge b-green plain">En cours</span>
            ),
          ])}
        />
      </Carte>
      <div className="grid-2">
        <Carte titre="Balance âgée" flush={false}>
          <Ligne libelle="0–30 jours" valeur={fcfa(age.j0_30)} />
          <Ligne libelle="30–60 jours" valeur={fcfa(age.j30_60)} />
          <Ligne libelle="60–90 jours" valeur={fcfa(age.j60_90)} />
          <Ligne libelle="Plus de 90 j — provisionné (491)" valeur={fcfa(age.plus90)} couleur={age.plus90 ? 'var(--err)' : undefined} />
          <Ligne libelle="Total créances" valeur={fcfa(age.total)} fort />
        </Carte>
        <Carte titre="Produits constatés d’avance (487)" icone="clock" sous="Encaissé mais pas encore du CA" flush={false}>
          <Ligne libelle="Solde 487 (écritures manuelles)" valeur={fcfa(pca)} fort />
          <p className="t-muted" style={{ marginTop: 10, fontSize: 12 }}>
            Aucune source automatique : la console n’enregistre ni packs prépayés ni abonnements annuels (voir « Périmètre »).
            Un encaissement d’avance se passe en écriture manuelle (débit 521 ou 551, crédit 487), puis se reconnaît au fil
            des prestations (débit 487, crédit 7061).
          </p>
        </Carte>
      </div>
    </>
  )
}

/* ---------------------------------------------------------- plan comptable */

export function PlanComptable({ donnees, soldes }: PropsCompta) {
  const plan = planComplet(donnees.planComptable)
  const solde = new Map(soldes.map((s) => [s.code, s.solde]))
  const parClasse = new Map<number, typeof plan>()
  for (const c of plan) parClasse.set(classeCompte(c.code), [...(parClasse.get(classeCompte(c.code)) ?? []), c])
  return (
    <>
      <div className="bandeau" style={{ marginBottom: 14 }}>
        Plan comptable SYSCOHADA révisé, classes 1 à 9. Les lignes surlignées sont mouvementées. Renommer un compte ou en
        ajouter un : onglet « Paramètres ».
      </div>
      {[...parClasse.keys()]
        .sort((a, b) => a - b)
        .map((k) => {
          const liste = parClasse.get(k) ?? []
          const mvt = liste.filter((c) => solde.has(c.code)).length
          return (
            <Carte key={k} titre={CLASSES_SYSCOHADA[k] ?? `Classe ${k}`} sous={`${liste.length} comptes${mvt ? ` · ${mvt} mouvementé${mvt > 1 ? 's' : ''}` : ''}`}>
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Code</th>
                      <th>Libellé</th>
                      <th style={{ textAlign: 'right', width: 160 }}>Solde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liste.map((c) => {
                      const s = solde.get(c.code)
                      return (
                        <tr key={c.code} style={s !== undefined ? { background: 'var(--gold-wash)' } : undefined}>
                          <td>
                            <CodeCompte code={c.code} />
                          </td>
                          <td>
                            {c.label}
                            {donnees.planComptable[c.code] && <span className="t-muted"> (renommé)</span>}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{s !== undefined ? DR_CR(s) : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Carte>
          )
        })}
    </>
  )
}

/* ------------------------------------------- répartition du 7061 par nature */

/**
 * Le compte 7061 (HT) par nature de revenu : forfaits / option longue distance / dépassements / suppléments hors
 * créneau / longue distance / courses facturées à l'unité / courses des particuliers. Le total est CELUI du 7061
 * des écritures (hors écritures manuelles) : un écart serait un défaut, pas une approximation.
 */
export function RepartitionVue({ donnees }: { donnees: PropsCompta['donnees'] }) {
  const r = repartitionCA(donnees)
  return (
    <Carte titre="Chiffre d’affaires 7061 par nature" icone="receipt" sous="Hors taxes · factures émises ou réglées (par ligne) + courses de particuliers constatées à la livraison" flush>
      <Table
        entetes={['Nature', 'Montant HT', 'Part']}
        droite={[1, 2]}
        vide="Aucun chiffre d’affaires"
        lignes={LIBELLE_REPARTITION.map((l) => [
          l.libelle,
          <span className="mono">{fcfa(r[l.cle])}</span>,
          r.total > 0 ? `${(Math.round((r[l.cle] / r.total) * 1000) / 10).toLocaleString('fr-FR')} %` : '—',
        ])}
        pied={['Total 7061', fcfa(r.total), r.total > 0 ? '100 %' : '—']}
      />
    </Carte>
  )
}
